/* eslint-disable no-debugger, no-restricted-imports */
import { FC, useEffect, useState } from 'react'

import { Icon, Modal, ModalProvider } from '@joor/design-system'
import { useAuth } from 'context/AuthProvider'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import { OrderStatus } from '__generated__/atlas-types'

import { Flex, Spacer } from 'styles/components'
import { FORMAT_MM_DD_YYYY, formatDate } from 'utils/dates'
import { useUserAnalytics } from 'utils/sdks/userAnalytics'
import { fromGlobalId, toGlobalId } from 'utils/transformations/graphql'

import usePortal from 'hooks/usePortal'
import {
  getCurrentUser,
  isAccountTypeRetailer,
  userIsLoggedIn,
} from 'store/currentUser/selectors'

import { Grid, Loader } from 'components/Core'

import { useGetLocalCurrenciesFF } from '../Review/components/PayView/components/Invoice/invoice.hooks'
import { WarningBanner } from '../components/WarningBanner/WarningBanner'
import { BillingAddressDictionary, Order } from '../types'
import {
  ContainerRow,
  InvoiceContainer,
  InvoiceHeader,
  InvoiceInfoRow,
  WarningBannerWrapper,
} from './PublicInvoice.ui'
import BillingInfo from './components/BillingInfo'
import DownloadInvoice from './components/DownloadInvoice'
import FinancialSummary from './components/FinancialSummary'
import ManualTransactions from './components/ManualTransactions'
import OrderDetails from './components/OrderDetails'
import Payment from './components/Payment'
import SalesRep from './components/SalesRep'
import ShippingInfo from './components/ShippingInfo'
import TransactionHistory from './components/TransactionHistory'
import { getCurrencyByCountry } from './components/helpers'
import { StatusPill } from 'features/Orders/components'
import { pillTextByStatus } from 'features/Orders/utils/helpers'

const StyledModal = styled(Modal)`
  max-height: 207px;
`

interface PropsType {
  order: Order
  orderQueryLoading: boolean
  billingAddressDictionary: BillingAddressDictionary
}

const PublicInvoice: FC<PropsType> = (props) => {
  const { order, orderQueryLoading } = props
  const localCurrency = getCurrencyByCountry(order.billingCountry)
  const [forceRerenderDate, setForceRerenderDate] = useState(new Date())
  const [canConvertCurrency, setCanConvertCurrency] = useState(false)
  const { trackEvent } = useUserAnalytics()
  const { uuid } = useParams<{
    uuid?: string
  }>()
  const { login } = useAuth()

  const Portal = usePortal()
  const currentUser = useSelector(getCurrentUser)
  const [isModalOpen, setIsModalOpen] = useState(true)

  // TODO: figure out if we do indeed need to keep both ids around or
  // if we should just move everything to the retailerId.
  const accountId = toGlobalId('Account', fromGlobalId(order.brand.id).id) ?? ''
  const retailerAccountId =
    toGlobalId('Account', fromGlobalId(order.retailer!.id).id) ?? ''
  const isUserLoggedIn = useSelector(userIsLoggedIn)
  const isRetailer = useSelector(isAccountTypeRetailer)

  useEffect(() => {
    if (uuid) {
      trackEvent('JP_Retailer_Email')
    } else if (isRetailer) {
      trackEvent('JP_Retailer_InPlatform')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data: shouldUseLocalCurrencies, loading } = useGetLocalCurrenciesFF({
    brandId: order.brand.id,
    skipWhen: localCurrency === order.currencyCode,
  })

  if (loading) {
    return <Loader active />
  }

  const showCancelledWarning = order.status === OrderStatus.CANCELLED

  const handleCanCovertCurrency = (canConvert: boolean) => {
    setCanConvertCurrency(canConvert)
  }

  return (
    <InvoiceContainer>
      <InvoiceHeader>
        <div className="title">
          <Icon iconName="shopping-bag" plain iconSize="small" />{' '}
          {order.referenceNumber ? `#${order.referenceNumber} – ` : ''}
          {order.collectionNames}
        </div>
        <span className="info">
          Last updated{' '}
          {formatDate(order.modified, `${FORMAT_MM_DD_YYYY} [at] h:MM:SS a`)}
          {order.lastModifier ? ` by ${order.lastModifier.email}` : ''}
        </span>
      </InvoiceHeader>
      <InvoiceInfoRow showDownloadInvoice={isUserLoggedIn}>
        <div>
          <div className="title">PO #</div>
          <div className="info">{order.referenceNumber ?? '—'}</div>
        </div>
        <div>
          <div className="title">Brand</div>
          <div className="info">{order.brandName}</div>
        </div>
        <div>
          <div className="title">Price Type</div>
          <div className="info">{order.currencyCode}</div>
        </div>
        <div>
          <div className="title">Status</div>
          <div className="info">
            {order.status && (
              <StatusPill
                //@ts-ignore check
                value={pillTextByStatus[order.status]}
                status={order.status}
              />
            )}
          </div>
        </div>
        {isUserLoggedIn && (
          <div style={{ alignSelf: 'center' }}>
            <DownloadInvoice orderId={order.id} />
          </div>
        )}
      </InvoiceInfoRow>
      <Grid container>
        <Grid.Row>
          <Grid.Col sm={12} md={12} lg={16}>
            {showCancelledWarning && (
              <Grid.Row>
                <Grid.Col textAlign="right">
                  <WarningBannerWrapper>
                    <WarningBanner text="This order was cancelled" />
                  </WarningBannerWrapper>
                </Grid.Col>
              </Grid.Row>
            )}
            <ContainerRow>
              <Grid.Col sm={12} md={6} lg={8}>
                <Flex flexDirection="column" rowGap="20px" alignItems="stretch">
                  <TransactionHistory
                    orderId={order.id}
                    accountId={retailerAccountId}
                    currencyCode={order.currencyCode}
                    shouldUseLocalCurrencies={
                      shouldUseLocalCurrencies && canConvertCurrency
                    }
                    billingCountry={order.billingCountry}
                  />
                  <ManualTransactions orderInfo={order} accountId={accountId} />
                  <Grid.Row>
                    <Grid.Col sm={12} md={3} lg={4}>
                      <Flex
                        flexDirection="column"
                        rowGap="20px"
                        alignItems="stretch"
                        margin="0 0 20px"
                      >
                        <OrderDetails order={order} />
                        <BillingInfo order={order} />
                        <SalesRep order={order} />
                      </Flex>
                    </Grid.Col>
                    <Grid.Col sm={12} md={3} lg={4}>
                      <Flex
                        flexDirection="column"
                        rowGap="20px"
                        alignItems="stretch"
                      >
                        <FinancialSummary
                          order={order}
                          retailerId={retailerAccountId}
                          brandId={accountId}
                        />
                        <ShippingInfo order={order} />
                      </Flex>
                    </Grid.Col>
                  </Grid.Row>
                </Flex>
              </Grid.Col>
              <Grid.Col sm={12} md={6} lg={8}>
                <Payment
                  {...props}
                  retailerId={order?.retailer?.id}
                  brandId={order?.brand?.id}
                  key={`${forceRerenderDate}`}
                  loading={orderQueryLoading}
                  shouldUseLocalCurrencies={shouldUseLocalCurrencies}
                  setForceRerenderDate={setForceRerenderDate}
                  disablePayment={order.status === OrderStatus.CANCELLED}
                  uuid={uuid}
                  onRatesFetched={handleCanCovertCurrency}
                />
              </Grid.Col>
            </ContainerRow>
          </Grid.Col>
        </Grid.Row>
        <Spacer topMargin="120px" />
      </Grid>
      <Portal>
        <ModalProvider>
          {!currentUser?.account?.accountId && (
            <StyledModal
              title={{ text: 'Get more out of JOOR' }}
              subtitle={`Logging in gives you access to full order details 
              and the ability to save payment information for future transactions.`}
              isOpen={isModalOpen}
              disableBackgroundClick
              onClose={() => setIsModalOpen(false)}
              size="medium"
            >
              <Modal.Actions
                primaryButton={{
                  text: 'Login',
                  handler: () => {
                    login()
                  },
                }}
                secondaryButtonText="Continue as a Guest"
              />
            </StyledModal>
          )}
        </ModalProvider>
      </Portal>
    </InvoiceContainer>
  )
}

export default PublicInvoice
